import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import { type PointerEvent, type RefObject, useRef } from 'react';
import useOutline from './use-outline';
type OutlineRailProps = {
  scrollRef: RefObject<HTMLElement | null>;
  contentRef: RefObject<HTMLElement | null>;
};

// GROWTH is 1 so a mark tops out at exactly twice `w-4`, which is where the card's `left-10` sits.
const REACH = 30;
const GROWTH = 1;
const smoothstep = (t: number) => t * t * (3 - 2 * t);

/** Mount it beside the scroller, never inside it, so it holds still while the page moves. */
const OutlineRail = t0 => {
  const $ = _c(10);
  const {
    scrollRef,
    contentRef
  } = t0;
  const {
    entries,
    activeIndex,
    jumpTo
  } = useOutline(scrollRef, contentRef);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const marks = useRef(t1);
  const queued = useRef(0);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = pointerY => {
      const nodes = marks.current.filter(_temp);
      if (pointerY === null) {
        for (const mark_0 of nodes) {
          mark_0.style.transform = "";
        }
        return;
      }
      const centres = nodes.map(_temp2);
      nodes.forEach((mark_2, index) => {
        const nearness = smoothstep(Math.max(0, 1 - Math.abs(centres[index] - pointerY) / REACH));
        mark_2.style.transform = `scaleX(${1 + GROWTH * nearness})`;
      });
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const magnify = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = event => {
      const {
        clientY
      } = event;
      if (queued.current) {
        return;
      }
      queued.current = requestAnimationFrame(() => {
        queued.current = 0;
        magnify(clientY);
      });
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const handlePointerMove = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      if (queued.current) {
        cancelAnimationFrame(queued.current);
      }
      queued.current = 0;
      magnify(null);
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const handlePointerLeave = t4;
  if (entries.length < 2) {
    return null;
  }
  let t5;
  if ($[4] !== activeIndex || $[5] !== entries || $[6] !== jumpTo) {
    t5 = entries.map((entry, index_0) => {
      const isActive = index_0 === activeIndex;
      return <button aria-current={isActive || undefined} className="group/mark relative flex h-3.5 w-8 items-center justify-start" key={index_0} onClick={() => jumpTo(index_0)} type="button"><span aria-hidden={true} className={cx("h-0.5 w-4 origin-left rounded-full transition-[transform,background-color] duration-150 ease-out motion-reduce:transition-none", isActive ? "bg-tertiary group-hover/rail:bg-senary" : "bg-senary", "group-hover/mark:bg-tertiary!")} ref={node => {
          marks.current[index_0] = node;
        }} /><span className={cx("pointer-events-none absolute left-10 top-1/2 w-max max-w-64 -translate-y-1/2 origin-left rounded-lg border border-tertiary bg-primary px-3 py-2 text-left shadow-md", "scale-[0.97] opacity-0 transition-[opacity,transform] duration-150 ease-out", "group-hover/mark:scale-100 group-hover/mark:opacity-100", "group-focus-visible/mark:scale-100 group-focus-visible/mark:opacity-100", "motion-reduce:transition-none")}><span className="block truncate text-label-3 text-primary">{entry.text}</span>{entry.preview && <span className="mt-0.5 line-clamp-2 text-caption-2 text-tertiary">{entry.preview}</span>}</span></button>;
    });
    $[4] = activeIndex;
    $[5] = entries;
    $[6] = jumpTo;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = <nav aria-label="Page sections" className="group/rail absolute left-4 top-1/2 z-20 hidden -translate-y-1/2 flex-col items-start @[52rem]:flex" onPointerLeave={handlePointerLeave} onPointerMove={handlePointerMove}>{t5}</nav>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
};
export default OutlineRail;
function _temp(mark) {
  return !!mark;
}
function _temp2(mark_1) {
  const box = mark_1.getBoundingClientRect();
  return box.top + box.height / 2;
}